import { createConsumer, Consumer, Subscription } from "@rails/actioncable";
import { ActionCableConnectionStatus } from "./types";

class ActionCableClient {
  private static instance: ActionCableClient;
  private consumer: Consumer | null = null;
  private subscriptions: Map<string, Subscription> = new Map();
  private isInitialized = false;
  private connectionStatus: ActionCableConnectionStatus = {
    state: "disconnected",
    isConnected: false,
  };

  public static getInstance(): ActionCableClient {
    if (!ActionCableClient.instance) {
      ActionCableClient.instance = new ActionCableClient();
    }
    return ActionCableClient.instance;
  }

  public initialize(baseUrl: string, cartToken: string): void {
    const connectionUrl = `${baseUrl}?cart_token=${cartToken}&unauthenticated=true`;

    try {
      if (this.consumer) {
        this.disconnect();
      }

      this.consumer = createConsumer(connectionUrl);
      this.isInitialized = true;
      this.connectionStatus = {
        state: "connected",
        isConnected: true,
      };
    } catch (error) {
      console.error("[ActionCable] Initialization failed:", error);
      this.connectionStatus = {
        state: "disconnected",
        isConnected: false,
        error: error as Error,
      };
      throw error;
    }
  }

  public subscribe<T>(
    channelName: string,
    params: Record<string, string>,
    callbacks: {
      received: (data: T) => void;
      connected?: () => void;
      disconnected?: () => void;
      rejected?: () => void;
    },
  ): Subscription | null {
    if (!this.consumer) {
      console.error("[ActionCable] Not initialized");
      return null;
    }

    const subscriptionKey = `${channelName}-${params.cart_token}`;

    if (this.subscriptions.has(subscriptionKey)) {
      return this.subscriptions.get(subscriptionKey)!;
    }

    try {
      const subscription = this.consumer.subscriptions.create(
        { channel: channelName, ...params },
        {
          received: (data: T) => {
            callbacks.received(data);
          },
          connected: () => {
            this.connectionStatus = {
              state: "connected",
              isConnected: true,
            };
            callbacks.connected?.();
          },
          disconnected: () => {
            console.warn(`[ActionCable] Disconnected from ${channelName}`);
            this.connectionStatus = {
              state: "disconnected",
              isConnected: false,
            };
            callbacks.disconnected?.();
          },
          rejected: () => {
            console.error(
              `[ActionCable] Subscription rejected for ${channelName}`,
            );
            callbacks.rejected?.();
          },
        },
      );

      this.subscriptions.set(subscriptionKey, subscription);
      return subscription;
    } catch (error) {
      console.error(
        `[ActionCable] Failed to subscribe to ${channelName}:`,
        error,
      );
      return null;
    }
  }

  public unsubscribe(channelName: string, cartToken: string): void {
    const subscriptionKey = `${channelName}-${cartToken}`;
    const subscription = this.subscriptions.get(subscriptionKey);

    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(subscriptionKey);
    }
  }

  public disconnect(): void {
    if (this.consumer) {
      this.subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
      this.subscriptions.clear();

      this.consumer.disconnect();
      this.consumer = null;
      this.isInitialized = false;
      this.connectionStatus = {
        state: "disconnected",
        isConnected: false,
      };
    }
  }

  public getConnectionStatus(): ActionCableConnectionStatus {
    return this.connectionStatus;
  }

  public isConnected(): boolean {
    return this.connectionStatus.isConnected;
  }

  public getIsInitialized(): boolean {
    return this.isInitialized;
  }
}

export const actionCableClient = ActionCableClient.getInstance();
